import { useMemo, useState } from "react";
import type { ComponentProps } from "react";

import { Table } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Box } from "@similarweb/ui-components/foundations";

import type { IFlattenedCategory } from "app/common/services/categoryService.types";
import { translate } from "app/filters/i18nFilter";
import { useDebouncedSetSearchTerm } from "app/pages/digital-marketing/ad-intelligence/common/ads/hooks/useDebouncedSetSearchTerm";
import { IndustryDataAvailabilityPageBanner } from "app/pages/digital-marketing/ad-intelligence/common/data-availability/IndustryDataAvailabilityBanner";
import { useDisplayAdvertiserPublisherDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useDisplayAdvertiserPublisherDataAndParams";
import type { IIndustryCampaignRow } from "app/pages/digital-marketing/ad-intelligence/common/overview/industry/top-campaigns/useIndustryTopCampaigns";
import { useIndustryTopCampaigns } from "app/pages/digital-marketing/ad-intelligence/common/overview/industry/top-campaigns/useIndustryTopCampaigns";
import {
    buildSearchFilter,
    FIRST_PAGE,
    getNoDataObject,
    PAGE_SIZE_LARGE,
} from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";
import type { ISortProps } from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";
import { useSelectedIndustry } from "app/pages/digital-marketing/ad-intelligence/shared/components/AssetPicker/useSelectedIndustry";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { getColumns } from "./IndustryCampaignsColumns";
import { IndustryCampaignsFilters } from "./IndustryCampaignsFilters";

// Industry endpoints use snake_case server sort keys (last_seen/first_seen/
// active_days on the ads endpoint); map the camelCase column keys to them.
const SERVER_SORT_KEY_BY_COLUMN: Record<string, string> = {
    campaignName: "campaign",
    creativesCount: "creatives_count",
    activeDays: "active_days",
    firstSeen: "first_seen",
    lastSeen: "last_seen",
};

// Industry mode defaults to the highest number of ads (creatives count) first,
// unlike domain mode which defaults to last seen.
const INDUSTRY_CAMPAIGNS_INITIAL_SORT: ISortProps = { sort: "creativesCount", asc: false };
export const INDUSTRY_CAMPAIGNS_DEFAULT_SERVER_SORT = SERVER_SORT_KEY_BY_COLUMN[INDUSTRY_CAMPAIGNS_INITIAL_SORT.sort];

const SEARCH_DEBOUNCE_DELAY = 600;

interface IIndustryCampaignsProps {
    category: IFlattenedCategory;
}

export const IndustryCampaigns = ({ category }: IIndustryCampaignsProps) => {
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const { replaceWithDomain } = useSelectedIndustry();
    const [currentPage, setCurrentPage] = useState(FIRST_PAGE);
    // `includeTerms` is an array route param (string[]); take the first term.
    const [search, setSearch] = useState<string>(params.includeTerms?.[0] ?? "");
    const [sort, setSort] = useState<ISortProps>({
        sort: params.sort ?? INDUSTRY_CAMPAIGNS_INITIAL_SORT.sort,
        asc: params.asc ? params.asc === "true" : INDUSTRY_CAMPAIGNS_INITIAL_SORT.asc,
    });
    // `selectedAdvertisers` is a declared campaigns-route param (string[] at runtime) - hydrated from the URL.
    const [selectedAdvertisers, setSelectedAdvertisers] = useState<string[]>(params.selectedAdvertisers ?? []);
    const debouncedSetSearchTerm = useDebouncedSetSearchTerm(SEARCH_DEBOUNCE_DELAY);

    const serverSortKey = SERVER_SORT_KEY_BY_COLUMN[sort.sort] ?? INDUSTRY_CAMPAIGNS_DEFAULT_SERVER_SORT;
    const { rows, totalCount, isLoading, isError, availability } = useIndustryTopCampaigns(
        category,
        currentPage,
        PAGE_SIZE_LARGE,
        search,
        serverSortKey,
        sort.asc,
        selectedAdvertisers,
    );

    const columns = useMemo(
        () => getColumns(totalCount, currentPage, PAGE_SIZE_LARGE, sort, replaceWithDomain),
        [totalCount, currentPage, sort, replaceWithDomain],
    );

    const handleSearchInputChange = (value: string) => {
        void proNavigator.updateQueryParams({ ...params, includeTerms: value }, { reload: false });
        allTrackers.dispatchTrackEvent("Search Bar", "click", `Table/${value}`);
        setCurrentPage(FIRST_PAGE);
        // Debounce the query-driving state so we don't refetch on every keystroke.
        debouncedSetSearchTerm(value, setSearch);
    };

    const handleAdvertisersFilterChange = (advertisers: string[]) => {
        setSelectedAdvertisers(advertisers);
        if (advertisers.length) {
            allTrackers.dispatchTrackEvent("Drop Down", "click", `Filter Advertiser/${advertisers.length} selected`);
        }
        void proNavigator.updateParams({ ...params, selectedAdvertisers: advertisers }, { reload: false });
        setCurrentPage(FIRST_PAGE);
    };

    const handleSort: ComponentProps<typeof Table<IIndustryCampaignRow>>["onSort"] = (sorter) => {
        void proNavigator.updateQueryParams(
            { ...params, sort: sorter.columnKey, asc: sorter.order === "ascend" },
            { reload: false },
        );
        setSort({ sort: sorter.columnKey as string, asc: sorter.order === "ascend" });
        setCurrentPage(FIRST_PAGE);
    };

    const noDataObject = getNoDataObject(isError);

    return (
        <>
            <IndustryDataAvailabilityPageBanner availability={availability} />
            <Box
                width="100%"
                height="initial"
                background={colorsPalettes.carbon[0]}
                borderRadius="6px"
                data-automation="industry-campaigns-table"
            >
                <Table
                    columnConfig={columns}
                    data={rows}
                    isLoading={isLoading}
                    loaderRowCount={13}
                    onSort={handleSort}
                    localStorageKey="AdIntelligenceIndustryCampaigns"
                    pagination={{
                        pageSize: PAGE_SIZE_LARGE,
                        total: totalCount,
                        current: currentPage,
                        onChange: (_page, _pageSize, nextPage) => setCurrentPage(nextPage),
                    }}
                    noData={noDataObject}
                >
                    <Table.TableTop borderBottom>
                        <Table.TableTop.Filters
                            filters={buildSearchFilter({
                                initialValue: search,
                                placeholder: translate("ad.intelligence.advertiser.campaigns.table.search.placeholder"),
                                onSearchChange: handleSearchInputChange,
                            })}
                            factoryFilters={
                                <IndustryCampaignsFilters
                                    category={category}
                                    selectedAdvertisers={selectedAdvertisers}
                                    onAdvertisersFilterChange={handleAdvertisersFilterChange}
                                />
                            }
                        />
                    </Table.TableTop>
                </Table>
            </Box>
        </>
    );
};
